import React from 'react';
import { useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroller';
import { debounce } from 'lodash';

import { useTranslation } from '@wix/yoshi-flow-editor';

import {
  selectSuggestedMembers,
  selectSuggestedMembersMetadata,
} from 'store/selectors';

import { useController } from 'common/context/controller';

import {
  ProfileListItem,
  ProfileListItemSkeleton,
} from 'common/components/ProfileListItem';

import { List } from 'wui/List';
import { EmptyState } from 'wui/EmptyState';
import { Button } from 'wui/Button';
import { ErrorState } from 'wui/ErrorState';

interface ISuggestedMembersProps {
  groupId: string;
  selected: string[];
  search: string;

  onReset(): void;
  onSelect(memberIds: string[]): void;
}

export function SuggestedMembersList(props: ISuggestedMembersProps) {
  const { selected, search, groupId, onReset, onSelect } = props;

  const { t } = useTranslation();

  const { members$ } = useController();

  const meta = useSelector(selectSuggestedMembersMetadata);
  const members = useSelector(selectSuggestedMembers);

  const querySuggested = React.useCallback(
    debounce((search: string) => {
      members$.querySuggested(groupId, {
        nickname: search,
      });
    }, 300),
    [],
  );

  React.useEffect(() => {
    querySuggested(search);
  }, [search]);

  if (meta.error) {
    return (
      <ErrorState
        bw
        onRetry={handleRetry}
        title={t('groups-web.error-state.title')}
        actionLabel={t('groups-web.error-state.retry.label')}
      />
    );
  }

  if (meta.loading) {
    return (
      <List disablePadding>
        {Array(5)
          .fill(0)
          .map((i) => (
            <ProfileListItemSkeleton withIcon key={i} />
          ))}
      </List>
    );
  }

  if (!members.length) {
    return (
      <EmptyState
        bw
        title={t('groups-web.dialogs.add-members.search.no-result.title')}
        subtitle={t('groups-web.dialogs.add-members.search.no-result.subtitle')}
        variant="section"
        action={
          <Button onClick={onReset} variant="basic">
            {t('groups-web.dialogs.add-members.search.no-result.button.label')}
          </Button>
        }
      />
    );
  }

  return (
    <InfiniteScroll
      initialLoad={false}
      useWindow={false}
      hasMore={meta.metadata.total! > members.length}
      loadMore={handleLoadMore}
      threshold={200}
      loader={
        <>
          <ProfileListItemSkeleton withIcon />
          <ProfileListItemSkeleton withIcon />
          <ProfileListItemSkeleton withIcon />
        </>
      }
    >
      <List disablePadding>
        {members.map((member) => (
          <ProfileListItem
            key={member.memberId}
            profile={member}
            selected={isMemberSelected(member.memberId as string)}
            onClick={(event) => {
              event.stopPropagation?.();
              handleToggle(member.memberId as string);
            }}
          />
        ))}
      </List>
    </InfiniteScroll>
  );

  function handleToggle(memberId: string) {
    const isSelected = selected.indexOf(memberId) !== -1;

    if (isSelected) {
      onSelect(props.selected.filter((id) => id !== memberId));
    } else {
      onSelect([...props.selected, memberId]);
    }
  }

  function isMemberSelected(memberId: string) {
    return selected.includes(memberId);
  }

  function handleLoadMore() {
    members$.querySuggested(
      groupId,
      {
        nickname: search,
      },
      members.length,
    );
  }

  function handleRetry() {
    members$.querySuggested(groupId, {
      nickname: search,
    });
  }
}

SuggestedMembersList.displayName = 'SuggestedMembersList';
